import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, useMap, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster/dist/leaflet.markercluster.js';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';
import { MemoryRouter, Link } from 'react-router-dom';
import './MapView.css';
import HearingRequestButton from './HearingRequestButton';

// Initialize default Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MarkerCluster = React.memo(({ markers, renderPopupContent }) => {
  const map = useMap();
  const markerClusterGroupRef = useRef(null);

  useEffect(() => {
    if (!map) return;

    // Initialize marker cluster group
    markerClusterGroupRef.current = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: false,
      removeOutsideVisibleBounds: true,
    });

    // Add markers to the cluster group
    markers.forEach((marker) => {
      const leafletMarker = L.marker([marker.lat, marker.lng]);
      const popupHTML = ReactDOMServer.renderToString(
        <MemoryRouter initialEntries={['/']}>
          {renderPopupContent(marker)}
        </MemoryRouter>
      );
      leafletMarker.bindPopup(popupHTML, { maxWidth: 400 });
      markerClusterGroupRef.current.addLayer(leafletMarker);
    });

    // Add the cluster group to the map
    map.addLayer(markerClusterGroupRef.current);

    // Cleanup function
    return () => {
      if (markerClusterGroupRef.current) {
        map.removeLayer(markerClusterGroupRef.current);
      }
    };
  }, [markers, map, renderPopupContent]);

  return null;
});

const MapView = () => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [searchResultsVisible, setSearchResultsVisible] = useState(false);

  // Define the initial map center and zoom level for Europe
  const initialCenter = useMemo(() => [50, 10], []);
  const initialZoom = 6;

  const fetchMarkers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/hifisetups/locations');
      if (!response.ok) {
        throw new Error('Failed to fetch setups');
      }
      const data = await response.json();
      setMarkers(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load HiFi setups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  useEffect(() => {
    const filtered = markers.filter(marker => {
      const searchableText = `${marker.username} ${marker.speakers} ${marker.amplifier} ${marker.preamp} ${marker.dac} ${marker.sources}`.toLowerCase();
      return searchableText.includes(searchQuery.toLowerCase());
    });
    setFilteredMarkers(filtered);
    setSearchResultsVisible(searchQuery.length > 0 && filtered.length > 0);
  }, [searchQuery, markers]);

  const renderPopupContent = useCallback((marker) => (
    <div className="setup-popup">
      <h3>{marker.username}'s Setup</h3>
      <table className="popup-table">
        <tbody>
          <tr>
            <td colSpan="2" className="hearing-request-cell">
              <HearingRequestButton userId={marker.id} />
              {(marker.image1_path || marker.image2_path) && (
                <>
                  {marker.image1_path && (
                    <a
                      href={marker.image1_path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src="/camera.png"
                        alt="Image 1"
                        style={{ width: '20px', height: '20px', verticalAlign: 'middle', marginLeft: '5px' }}
                      />
                    </a>
                  )}
                  {marker.image2_path && (
                    <a
                      href={marker.image2_path}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src="/camera.png"
                        alt="Image 2"
                        style={{ width: '20px', height: '20px', verticalAlign: 'middle', marginLeft: '5px' }}
                      />
                    </a>
                  )}
                </>
              )}
            </td>
          </tr>
          {marker.speakers && (
            <tr>
              <th>Speakers:</th>
              <td>{Array.isArray(marker.speakers) ? marker.speakers.join(', ') : marker.speakers}</td>
            </tr>
          )}
          {marker.amplifier && (
            <tr>
              <th>Amplifier:</th>
              <td>{Array.isArray(marker.amplifier) ? marker.amplifier.join(', ') : marker.amplifier}</td>
            </tr>
          )}
          {marker.preamp && (
            <tr>
              <th>Preamp:</th>
              <td>{Array.isArray(marker.preamp) ? marker.preamp.join(', ') : marker.preamp}</td>
            </tr>
          )}
          {marker.dac && (
            <tr>
              <th>DAC:</th>
              <td>{Array.isArray(marker.dac) ? marker.dac.join(', ') : marker.dac}</td>
            </tr>
          )}
          {marker.sources && (
            <tr>
              <th>Sources:</th>
              <td>{Array.isArray(marker.sources) ? marker.sources.join(', ') : marker.sources}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  ), []);

  const handleSearchResultClick = useCallback((marker) => {
    mapRef.current.flyTo([marker.lat, marker.lng], 12);
  }, []);

  const tileLayerOptions = useMemo(() => ({
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
    detectRetina: true,
    tileSize: 512,
    zoomOffset: -1,
    reuseTiles: true,
    updateWhenIdle: false,
  }), []);

  if (loading) return <div className="map-loading">Loading HiFi setups...</div>;
  if (error) return <div className="map-error">{error}</div>;

  return (
    <div className="map-wrapper">
      <div className="search-overlay">
        <div className="search-input-wrapper">
          <span className="material-symbols-outlined search-icon">search</span>
          <input
            type="text"
            placeholder="Search setups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        {searchResultsVisible && (
          <div className="search-results">
            {filteredMarkers.length > 0 ? (
              <ul>
                {filteredMarkers.map(marker => (
                  <li key={marker.id}>
                    <Link to={`#setup-${marker.id}`} onClick={() => handleSearchResultClick(marker)}>
                      {marker.username}'s Setup
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No setups found.</p>
            )}
          </div>
        )}
      </div>
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        scrollWheelZoom={true}
        className="map-container"
        whenCreated={(map) => {
          mapRef.current = map;
          const tilePane = map.getPane('tilePane');
          if (tilePane) {
            tilePane.style.background = 'transparent';
          }
        }}
      >
        <TileLayer
          {...tileLayerOptions}
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MarkerCluster markers={filteredMarkers} renderPopupContent={renderPopupContent} />
      </MapContainer>
    </div>
  );
};

export default MapView;